<template>
    <div class="home-productivity">
        <header class="toolbar">
            <label class="mr-4 time-ranger">
                <span class="label">Time Range: </span>
                <custom-combo-box class="time-range-select" :options="timeRangeOptions"
                                  @userSelectOption="onTimeRangeSelectorChange" theme="light"
                                  :initial-value="activeTimeRangeOptionValue"></custom-combo-box>
            </label>
            <button class="mr-2 button-fancy button-fancy-light-purple" @click="expandAll">Expand All</button>
            <button class="button-fancy button-fancy-light-purple" @click="collapseAll">Collapse All</button>
        </header>
        <div class="main">
            <template v-if="showingResults">
                <template v-if="data.length">
                    <div class="table-container">
                        <table class="standard-table">
                            <thead>
                            <tr>
                                <th>
                                    <div>User</div>
                                </th>
                                <th>
                                    <div>Project</div>
                                </th>
                                <th class="text-right">
                                    <div>Done</div>
                                </th>
                                <th class="text-right">
                                    <div>Skipped</div>
                                </th>
                                <th class="text-right">
                                    <div>Processed
                                        <span @mouseenter="onMouseEnterTooltipTarget"
                                              @mouseleave="onMouseLeaveTooltipTarget"
                                              data-tooltip-pos="up"
                                              data-tooltip="Including Done & Skipped"
                                        ><i class="gf gf-info"></i>
                                </span>
                                    </div>
                                </th>
                                <th class="text-right">
                                    <div>Processed Speed
                                        <span @mouseenter="onMouseEnterTooltipTarget"
                                              @mouseleave="onMouseLeaveTooltipTarget"
                                              data-tooltip-pos="up"
                                              data-tooltip="Number of processed documents per working day">
                                    <i class="gf gf-info"></i>
                                </span></div>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(item,index) in shownRows" :key="index"
                                :class="{'expanded-primary-row': item._is_expanded_primary_row, 'expanded-secondary-row': !item._is_primary_row}">
                                <template v-if="item._is_primary_row">
                                    <td>
                                        <div class="expand-mounter" @click="toggleRow(item)">
                                            <span class="text-capitalize">{{item.username}}</span>
                                            <span class="expand-toggle">
                                        <i class="gf gf-arrow-down" v-if="isRowExpanded(item)"></i>
                                        <i v-else class="gf gf-arrow-right"></i>
                                    </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div>{{item.count_projects}} Project<template v-if="item.count_projects>1">s</template></div>
                                    </td>
                                    <td class="text-right">
                                        <div>{{numberWithCommas(item.count_done)}}</div>
                                    </td>
                                    <td class="text-right">
                                        <div>{{numberWithCommas(item.count_skipped)}}</div>
                                    </td>
                                    <td class="text-right">
                                        <div>{{numberWithCommas(item.count_processed)}}</div>
                                    </td>
                                    <td class="text-right">
                                        <div>{{getSimplifiedNumber(item.speed)}}</div>
                                    </td>
                                </template>
                                <template v-else>
                                    <td>

                                    </td>
                                    <td>
                                        <div>{{projectIdToName[item.project_id]}}</div>
                                    </td>
                                    <td class="text-right">
                                        <div>{{numberWithCommas(item.count_done)}}</div>
                                    </td>
                                    <td class="text-right">
                                        <div>{{numberWithCommas(item.count_skipped)}}</div>
                                    </td>
                                    <td class="text-right">
                                        <div>{{numberWithCommas(item.count_processed)}}</div>
                                    </td>
                                    <td class="text-right">
                                        <div>{{getSimplifiedNumber(item.speed)}}</div>
                                    </td>
                                </template>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
                <template v-else>
                    <div class="global-message-box">
                        <div>No Productivity Data to Show.</div>
                    </div>
                </template>
            </template>
            <template v-else-if="isLoading">
                <div class="global-message-box">
                    <spinner></spinner>
                </div>
            </template>
            <template v-else-if="isHavingProblemLoading">
                <div class="global-message-box">
                    <span>There was something not right while loading the Productivity.</span>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
    import CustomComboBox from "@/InputControls/CustomComboBox";
    import {getProductivity} from "@/apis";
    import {numberWithCommas} from "@/utils";
    import {projectIdToName} from "@/config";
    import Spinner from "@/components/Spinner";

    const timeRangeOptions = [
        {
            label: 'All Time',
            value: 'all'
        },
        {
            label: 'Today',
            value: 'l1'
        },
        {
            label: 'Last 2 Days',
            value: 'l2'
        },
        {
            label: 'Last 7 Days',
            value: 'l7'
        },
        {
            label: 'Last 30 Days',
            value: 'l30'
        }
    ];

    export default {
        name: "HomeProductivity",
        components: {Spinner, CustomComboBox},
        methods: {
            getSimplifiedNumber(number) {
                number = Math.round(number);
                if (number >= 1) {
                    return numberWithCommas(number)
                }
                return '< 1';
            },
            onTimeRangeSelectorChange(value) {
                if (this.activeTimeRangeOptionValue === value) {
                    return;
                }
                this.activeTimeRangeOptionValue = value;
                this.loadData();
            },
            numberWithCommas: numberWithCommas,
            toggleRow(row) {
                const {username} = row;
                this.$set(this.expandedUsernameMaps, username, !this.expandedUsernameMaps[username]);
            },
            isRowExpanded(row) {
                if (row._is_primary_row) {
                    return !!this.expandedUsernameMaps[row.username];
                }
                return false;
            },
            expandAll() {
                const maps = {};
                this.users.forEach(v => {
                    maps[v] = true;
                })
                this.expandedUsernameMaps = maps;
            },
            collapseAll() {
                this.expandedUsernameMaps = {};
            },
            setLoadingStatus(isLoading, isHavingProblemLoading, showingResults) {
                this.isLoading = isLoading;
                this.isHavingProblemLoading = isHavingProblemLoading;
                this.showingResults = showingResults;
            },
            loadData() {
                this.setLoadingStatus(true, false, false);
                getProductivity(this.activeTimeRangeOptionValue).then(r => {
                    this.data = r.data;
                    this.setLoadingStatus(false, false, true);
                }).catch((err) => {
                    this.setLoadingStatus(false, true, false);
                    this.handleServerSideError(err);
                })
            },
            loadDataSilently() {
                getProductivity(this.activeTimeRangeOptionValue).then(r => {
                    this.data = r.data;
                }).catch((err) => {
                    this.handleServerSideError(err);
                })
            },
        },
        data() {
            return {
                projectIdToName,
                timeRangeOptions: timeRangeOptions,
                activeTimeRangeOptionValue: timeRangeOptions[0].value,
                data: [],
                expandedUsernameMaps: {},
                isLoading: false,
                isHavingProblemLoading: false,
                showingResults: false,
            }
        },
        mounted() {
            this.loadData();
        },
        computed: {
            rows() {
                let rows = [];
                let row;
                this.data.forEach(item => {
                    row = {};
                    row._is_primary_row = true;
                    const {username} = item;
                    const {count_processed, count_done, count_skipped, speed} = item.sum;
                    row.username = username;
                    row.count_processed = count_processed;
                    row.count_done = count_done;
                    row.count_skipped = count_skipped;
                    row.speed = speed;
                    row.count_projects = item.projects.length;

                    rows.push(row);

                    if (this.expandedUsernameMaps[username]) {
                        row._is_expanded_primary_row = true;
                        item.projects.forEach((project) => {
                            row = {};
                            const {count_processed, count_done, count_skipped, speed, project_id} = project;
                            row.count_processed = count_processed;
                            row.count_done = count_done;
                            row.count_skipped = count_skipped;
                            row.speed = speed;
                            row.project_id = project_id;
                            rows.push(row);
                        })
                    }
                });
                return rows;
            },
            shownRows() {
                return this.rows;
            },
            users() {
                return this.data.map(v => (v.username));
            },
        },
        watch: {
            $route(newVal, oldVal) {
                if (newVal.name === 'home' && oldVal.name !== 'home') {
                    this.loadDataSilently();
                }
            }
        }
    }
</script>

<style scoped lang="scss">
    @import "../assets/variables";

    .toolbar {
        display: flex;
        align-items: center;
        padding: 0 5rem 0 2rem;
        height: 90px;
    }

    .home-productivity {
        height: 100%;
    }

    .main {
        height: calc(100% - 90px);
        padding: 0 1rem;
    }

    .time-ranger {
        display: flex;
        align-items: center;
        .time-range-select {
            color: $darker-purple;
        }
    }

    .table-container {
        overflow: auto;
        height: 100%;
    }

    table {
        thead {
            th {
                text-align: left;

                .gf-info {
                    font-size: 0.875em;
                    opacity: 0.4;

                    &:hover {
                        opacity: 0.8;
                    }
                }

                div {
                    position: relative;

                    span[data-tooltip] {
                        display: block;
                        position: absolute;
                        right: -1em;
                        top: 0;
                    }
                }
            }
        }

        tbody {
            td {
                height: 2.5rem;
            }

            tr {
                &.expanded-primary-row {
                    td {
                        font-weight: 600;
                    }
                }

                &.expanded-secondary-row {
                    td {
                        color: #383838;
                    }
                }
            }
        }

        .expand-mounter {
            position: relative;
            cursor: pointer;
            &:hover {
                .expand-toggle {
                    opacity: 1;
                }
            }
        }

        .text-right {
            text-align: right;
        }

        .expand-toggle {
            position: absolute;
            left: 0;
            margin-left: -1rem;
            font-size: 0.86em;
            line-height: 1rem;
            top: 0;
            transform: scale(0.8);
            margin-top: 4px;
            opacity: 0.5;
            color: $darker-purple;
        }
    }
</style>
<style lang="scss">
    @import "../assets/variables";

    .time-range-select {
        .simple-dropdown {
            .trigger-button {
                border-radius: 999px;
                padding: 4px 11px;
                transition: color 0.2s, background-color 0.2s;
                font-weight: 500;

                &:hover {
                    background-color: rgba($primary-purple, 0.1);
                    color: darken($primary-purple, 2);
                }
            }

            &.active {
                .trigger-button {
                    background-color: rgba($primary-purple, 0.1);
                    color: darken($primary-purple, 2);
                }
            }
        }
    }
</style>
